import React from 'react';
import { Cartesian3DAxes } from '../common/Cartesian3DAxes.jsx';
import { DraggablePoint } from '../common/DraggablePoint.jsx';

export const SphereCrossSectionComponent = ({ params, onParamChange, svgRef, showCoordinates }) => {
  const {
    centerX = 280,
    centerY = 150,
    sphereRadius = 100,
    diskRadius = 60,
    diskOffsetY = -77,
    showSphere = true,
    showDisk = true,
    showRadius = true,
    sphereColor = "rgba(139,69,19,0.8)",
    diskColor = "rgba(128,128,128,0.6)",
    radiusColor = "red",
    stroke = "black",
    strokeWidth = 2
  } = params;

  // Calculate disk position
  const diskCenterY = centerY + diskOffsetY;
  
  // Calculate the actual disk radius based on sphere cross-section
  // Using the formula: diskRadius = sqrt(R² - h²) where h is the offset from center
  const maxDiskRadius = Math.sqrt(Math.max(0, sphereRadius * sphereRadius - diskOffsetY * diskOffsetY));
  const actualDiskRadius = Math.min(diskRadius, maxDiskRadius);
  
  // Disk perspective (ellipse)
  const diskRy = actualDiskRadius * 0.3; // Perspective compression

  return (
    <g>
      {/* Sphere */}
      {showSphere && (
        <>
          {/* Main sphere circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={sphereRadius}
            fill={sphereColor}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
          
          {/* Shading gradient for 3D effect */}
          <defs>
            <radialGradient id="sphereGradient">
              <stop offset="30%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="100%" stopColor="rgba(139,69,19,0.8)" />
            </radialGradient>
          </defs>
          <circle
            cx={centerX}
            cy={centerY}
            r={sphereRadius}
            fill="url(#sphereGradient)"
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
          
          {/* Vertical center line */}
          <line
            x1={centerX}
            y1={centerY - sphereRadius}
            x2={centerX}
            y2={centerY + sphereRadius}
            stroke="gray"
            strokeWidth={1}
            strokeDasharray="3,3"
            opacity="0.5"
          />
        </>
      )}
      
      {/* Cross-section disk */}
      {showDisk && diskOffsetY >= -sphereRadius && diskOffsetY <= sphereRadius && (
        <>
          {/* Disk ellipse (perspective view) */}
          <ellipse
            cx={centerX}
            cy={diskCenterY}
            rx={actualDiskRadius}
            ry={diskRy}
            fill={diskColor}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
          
          {/* Disk outline */}
          <ellipse
            cx={centerX}
            cy={diskCenterY}
            rx={actualDiskRadius}
            ry={diskRy}
            fill="none"
            stroke="darkgray"
            strokeWidth={strokeWidth * 1.5}
          />
          
          {/* Vertical line from center to disk */}
          <line
            x1={centerX}
            y1={centerY}
            x2={centerX}
            y2={diskCenterY}
            stroke="blue"
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
          />
          
          {/* Label for offset distance */}
          <text
            x={centerX + 10}
            y={(centerY + diskCenterY) / 2}
            fontSize="11"
            fill="blue"
            fontWeight="bold"
          >
            h = {Math.abs(diskOffsetY).toFixed(0)}
          </text>
        </>
      )}
      
      {/* Sphere radius */}
      {showRadius && (
        <>
          <line
            x1={centerX}
            y1={centerY}
            x2={centerX + sphereRadius}
            y2={centerY}
            stroke={radiusColor}
            strokeWidth={strokeWidth}
          />
          <text
            x={centerX + sphereRadius / 2}
            y={centerY - 10}
            fontSize="12"
            fill={radiusColor}
            fontWeight="bold"
          >
            R = {sphereRadius.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Disk radius (if disk is shown) */}
      {showDisk && diskOffsetY >= -sphereRadius && diskOffsetY <= sphereRadius && (
        <>
          <line
            x1={centerX}
            y1={diskCenterY}
            x2={centerX + actualDiskRadius}
            y2={diskCenterY}
            stroke="green"
            strokeWidth={strokeWidth}
          />
          <text
            x={centerX + actualDiskRadius / 2}
            y={diskCenterY - 8}
            fontSize="11"
            fill="green"
            fontWeight="bold"
          >
            r = {actualDiskRadius.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Formula annotation */}
      <text
        x={centerX - sphereRadius - 80}
        y={centerY - sphereRadius - 20}
        fontSize="12"
        fill="black"
        fontWeight="bold"
      >
        Cross-section: r² = R² - h²
      </text>
      
      {/* Draggable points */}
      {onParamChange && (
        <>
          <DraggablePoint
            cx={centerX}
            cy={centerY}
            label="Center"
            coords={showCoordinates ? `(${centerX}, ${centerY})` : ''}
            onDrag={(newX, newY) => {
              onParamChange('centerX', newX);
              onParamChange('centerY', newY);
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={centerX + sphereRadius}
            cy={centerY}
            label="R"
            coords={showCoordinates ? `R=${sphereRadius.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newRadius = Math.abs(newX - centerX);
              onParamChange('sphereRadius', Math.max(30, Math.min(150, newRadius)));
            }}
            svgRef={svgRef}
          />
          {diskOffsetY >= -sphereRadius && diskOffsetY <= sphereRadius && (
            <DraggablePoint
              cx={centerX}
              cy={diskCenterY}
              label="Disk"
              coords={showCoordinates ? `h=${Math.abs(diskOffsetY).toFixed(0)}` : ''}
              onDrag={(newX, newY) => {
                const newOffset = newY - centerY;
                onParamChange('diskOffsetY', Math.max(-sphereRadius, Math.min(sphereRadius, newOffset)));
              }}
              svgRef={svgRef}
            />
          )}
        </>
      )}
    </g>
  );
};
